import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAnalyticsPage } from "../context/AnalyticsPageContext";
import { useRegisterAnalyticsTabExport } from "../context/AnalyticsTabExportContext";
import { useOperationsAnalytics } from "../hooks/useOperationsAnalytics";
import { RevenueChartCard } from "./revenue/RevenueChartCard";
import {
  computeOperationsChartYMax,
  mapApprovalTimeChart,
  mapComplaintsChart,
  mapOnHoldCanceledChart,
  mapOperationsExecutionTrend,
  mapOperationsRequestsSection,
  mapOperationsSummary,
  mapTotalRequestsTrendChart,
} from "./operations/operationsTabMappers";
import {
  downloadOperationsApprovalTimeExport,
  downloadOperationsComplaintsExport,
  downloadOperationsOnHoldCanceledExport,
  downloadOperationsOnTimeRateExport,
  downloadOperationsProcessingTimeExport,
  downloadOperationsTotalRequestsExport,
  downloadOperationsUnderestimatedServicesExport,
} from "./operations/operationsTabExport";
import { OperationsExecutionWidget } from "./operations/OperationsExecutionWidget";
import { OperationsMetricChartsSection } from "./operations/OperationsMetricChartsSection";
import { OperationsRequestsSectionWidget } from "./operations/OperationsRequestsSectionWidget";
import { SERVICES_CONTENT_GAP } from "./services/constants";

export const OperationsTab: React.FC = () => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const operations = useOperationsAnalytics(dateRange);
  const [isExporting, setIsExporting] = useState(false);

  const summary = useMemo(
    () =>
      mapOperationsSummary(
        operations.totalRequests.data,
        operations.processingTime.data,
        operations.onTimeRate.data,
        operations.complaints.data,
        {
          totalRequests: operations.totalRequestsPrevious.data,
          processingTime: operations.processingTimePrevious.data,
          onTimeRate: operations.onTimeRatePrevious.data,
          complaints: operations.complaintsPrevious.data,
        },
      ),
    [
      operations.totalRequests.data,
      operations.processingTime.data,
      operations.onTimeRate.data,
      operations.complaints.data,
      operations.totalRequestsPrevious.data,
      operations.processingTimePrevious.data,
      operations.onTimeRatePrevious.data,
      operations.complaintsPrevious.data,
    ],
  );

  const executionTrend = useMemo(
    () =>
      mapOperationsExecutionTrend(
        operations.onTimeRate.data,
        operations.processingTime.data,
      ),
    [operations.onTimeRate.data, operations.processingTime.data],
  );

  const requestsSection = useMemo(
    () =>
      mapOperationsRequestsSection(
        operations.totalRequests.data,
        operations.underestimatedServices.data,
      ),
    [operations.totalRequests.data, operations.underestimatedServices.data],
  );

  const totalRequestsTrendChart = useMemo(
    () => mapTotalRequestsTrendChart(operations.totalRequests.data),
    [operations.totalRequests.data],
  );

  const onHoldCanceledChart = useMemo(
    () => mapOnHoldCanceledChart(operations.onHoldCanceled.data),
    [operations.onHoldCanceled.data],
  );

  const approvalTimeChart = useMemo(
    () => mapApprovalTimeChart(operations.approvalTime.data),
    [operations.approvalTime.data],
  );

  const complaintsChart = useMemo(
    () => mapComplaintsChart(operations.complaints.data),
    [operations.complaints.data],
  );

  const totalRequestsTrendYMax = useMemo(
    () =>
      computeOperationsChartYMax(totalRequestsTrendChart.data, [
        "guestServices",
        "roomServices",
        "externalServices",
      ]),
    [totalRequestsTrendChart.data],
  );

  const onHoldCanceledYMax = useMemo(
    () =>
      computeOperationsChartYMax(onHoldCanceledChart.data, [
        "canceledRequests",
        "onHoldRequests",
      ]),
    [onHoldCanceledChart.data],
  );

  const approvalTimeYMax = useMemo(
    () =>
      computeOperationsChartYMax(
        approvalTimeChart.data,
        ["frontdeskApprovalTime"],
        15,
      ),
    [approvalTimeChart.data],
  );

  const complaintsYMax = useMemo(
    () => computeOperationsChartYMax(complaintsChart.data, ["complaintCount"]),
    [complaintsChart.data],
  );

  const exportDisabled =
    !operations.isReady || operations.isLoading || isExporting;

  const runExport = useCallback(
    async (
      download: (params: {
        hotelId: string;
        from: string;
        to: string;
        format: "csv" | "xlsx";
      }) => Promise<void>,
      format: "csv" | "xlsx",
      successMessage: string,
      errorMessage: string,
    ) => {
      if (!operations.isReady) {
        return;
      }

      setIsExporting(true);
      try {
        await download({
          hotelId: operations.hotelId,
          from: operations.from,
          to: operations.to,
          format,
        });
        toast.success(successMessage);
      } catch {
        toast.error(errorMessage);
      } finally {
        setIsExporting(false);
      }
    },
    [operations.from, operations.hotelId, operations.isReady, operations.to],
  );

  const handleExportTotalRequests = useCallback(
    (format: "csv" | "xlsx") =>
      runExport(
        downloadOperationsTotalRequestsExport,
        format,
        t(
          "analytics.operations.toast.totalRequestsExportSuccess",
          "Total requests export downloaded",
        ),
        t(
          "analytics.operations.toast.totalRequestsExportError",
          "Failed to export total requests",
        ),
      ),
    [runExport, t],
  );

  const handleExportProcessingTime = useCallback(
    (format: "csv" | "xlsx") =>
      runExport(
        downloadOperationsProcessingTimeExport,
        format,
        t(
          "analytics.operations.toast.processingTimeExportSuccess",
          "Processing time export downloaded",
        ),
        t(
          "analytics.operations.toast.processingTimeExportError",
          "Failed to export processing time",
        ),
      ),
    [runExport, t],
  );

  const handleExportOnTimeRate = useCallback(
    (format: "csv" | "xlsx") =>
      runExport(
        downloadOperationsOnTimeRateExport,
        format,
        t(
          "analytics.operations.toast.onTimeRateExportSuccess",
          "On-time rate export downloaded",
        ),
        t(
          "analytics.operations.toast.onTimeRateExportError",
          "Failed to export on-time rate",
        ),
      ),
    [runExport, t],
  );

  const handleExportUnderestimated = useCallback(
    (format: "csv" | "xlsx") =>
      runExport(
        downloadOperationsUnderestimatedServicesExport,
        format,
        t(
          "analytics.operations.toast.underestimatedExportSuccess",
          "Underestimated services export downloaded",
        ),
        t(
          "analytics.operations.toast.underestimatedExportError",
          "Failed to export underestimated services",
        ),
      ),
    [runExport, t],
  );

  const handleExportOnHoldCanceled = useCallback(
    (format: "csv" | "xlsx") =>
      runExport(
        downloadOperationsOnHoldCanceledExport,
        format,
        t(
          "analytics.operations.toast.onHoldCanceledExportSuccess",
          "On-hold and canceled export downloaded",
        ),
        t(
          "analytics.operations.toast.onHoldCanceledExportError",
          "Failed to export on-hold and canceled requests",
        ),
      ),
    [runExport, t],
  );

  const handleExportApprovalTime = useCallback(
    (format: "csv" | "xlsx") =>
      runExport(
        downloadOperationsApprovalTimeExport,
        format,
        t(
          "analytics.operations.toast.approvalTimeExportSuccess",
          "Approval time export downloaded",
        ),
        t(
          "analytics.operations.toast.approvalTimeExportError",
          "Failed to export approval time",
        ),
      ),
    [runExport, t],
  );

  const handleExportComplaints = useCallback(
    (format: "csv" | "xlsx") =>
      runExport(
        downloadOperationsComplaintsExport,
        format,
        t(
          "analytics.operations.toast.complaintsExportSuccess",
          "Complaints export downloaded",
        ),
        t(
          "analytics.operations.toast.complaintsExportError",
          "Failed to export complaints",
        ),
      ),
    [runExport, t],
  );

  const handleExportOperationsTab = useCallback(
    async (format: "csv" | "xlsx") => {
      if (exportDisabled) {
        return;
      }

      const params = {
        hotelId: operations.hotelId,
        from: operations.from,
        to: operations.to,
        format,
      };

      setIsExporting(true);
      try {
        await downloadOperationsTotalRequestsExport(params);
        await downloadOperationsProcessingTimeExport(params);
        await downloadOperationsOnTimeRateExport(params);
        await downloadOperationsUnderestimatedServicesExport(params);
        await downloadOperationsOnHoldCanceledExport(params);
        await downloadOperationsApprovalTimeExport(params);
        await downloadOperationsComplaintsExport(params);

        toast.success(
          t(
            "analytics.operations.toast.tabExportSuccess",
            "Operations analytics export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.operations.toast.tabExportError",
            "Failed to export operations analytics",
          ),
        );
      } finally {
        setIsExporting(false);
      }
    },
    [exportDisabled, operations.from, operations.hotelId, operations.to, t],
  );

  const exportRegistration = useMemo(
    () => ({
      canExport: !exportDisabled,
      onExport: handleExportOperationsTab,
    }),
    [exportDisabled, handleExportOperationsTab],
  );
  useRegisterAnalyticsTabExport(exportRegistration);

  const isContentPending = operations.isLoading || operations.isFetching;

  let mainContent: React.ReactNode;

  if (!operations.isReady) {
    mainContent = (
      <RevenueChartCard title={t("analytics.operations.title", "Operations")}>
        <p className="text-sm text-[#737373]">
          {t(
            "analytics.operations.missingFilters",
            "Select a hotel and date range to view operations analytics.",
          )}
        </p>
      </RevenueChartCard>
    );
  } else if (operations.isError) {
    mainContent = (
      <RevenueChartCard title={t("analytics.operations.title", "Operations")}>
        <p className="text-sm text-[#737373]">
          {t(
            "analytics.operations.loadError",
            "Unable to load operations analytics. Try again.",
          )}
        </p>
      </RevenueChartCard>
    );
  } else {
    mainContent = (
      <>
        <OperationsExecutionWidget
          summary={summary}
          executionTrend={executionTrend}
          onExportProcessingTime={handleExportProcessingTime}
          onExportOnTimeRate={handleExportOnTimeRate}
          exportDisabled={exportDisabled}
        />

        <OperationsRequestsSectionWidget
          data={requestsSection}
          dateRange={dateRange}
          onExportCategories={handleExportTotalRequests}
          onExportUnderestimated={handleExportUnderestimated}
          exportDisabled={exportDisabled}
        />

        <OperationsMetricChartsSection
          totalRequestsTrendChart={totalRequestsTrendChart}
          totalRequestsTrendYMax={totalRequestsTrendYMax}
          onHoldCanceledChart={onHoldCanceledChart}
          onHoldCanceledYMax={onHoldCanceledYMax}
          approvalTimeChart={approvalTimeChart}
          approvalTimeYMax={approvalTimeYMax}
          complaintsChart={complaintsChart}
          complaintsYMax={complaintsYMax}
          onExportTotalRequests={handleExportTotalRequests}
          onExportOnHoldCanceled={handleExportOnHoldCanceled}
          onExportApprovalTime={handleExportApprovalTime}
          onExportComplaints={handleExportComplaints}
          exportDisabled={exportDisabled}
        />
      </>
    );
  }

  return (
    <section className="space-y-6 font-sans">
      <div
        className={cn(
          "flex w-full max-w-full flex-col transition-opacity duration-150",
          isContentPending && "opacity-70",
        )}
        style={{ gap: SERVICES_CONTENT_GAP }}
      >
        {mainContent}
      </div>
    </section>
  );
};
